/**
 * Estado de "Simular venda" e as réguas de fluxo que ela (e o Cenário do
 * PCP) usam. A entrada da simulação mora na URL (`?simular=`), para o link
 * copiado abrir a mesma simulação em outro aparelho, com outro perfil.
 * Nada aqui grava no backend.
 */
import { useCallback, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { useDados } from "../../context/DadosContext";
import {
  codificarSimulacao,
  decodificarSimulacao,
  folgaTerminoCarregamento,
  prazoDeFluxo,
  simularVenda,
} from "../../utils/capacidade";

export const PARAMETRO_SIMULACAO = "simular";

const SEM_FERIADOS = Object.freeze([]);

/**
 * Calendário da PLANILHA (aba TABELAS), que o Excel usa no WORKDAY de CO e
 * CR. Vem do extrato; um extrato antigo em cache (sem o campo) conta como
 * "sem feriados", sem o aviso de aba ausente.
 */
export function useFeriadosDaPlanilha() {
  const { extratoCapacidade } = useDados();
  return {
    feriadosPlanilha: extratoCapacidade?.feriadosPlanilha || SEM_FERIADOS,
    tabelasAusente: extratoCapacidade?.tabelasAusente === true,
  };
}

const SEM_FLUXO = Object.freeze({ fluxo: { suficiente: false, n: 0 }, folga: { G: 0, n: 0, suficiente: false } });

/**
 * Prazo de fluxo (venda → carregamento) e folga G (término → carregamento)
 * medidos na planilha atual + histórico guardado.
 */
export function useReguasDoFluxo({ painel, terminosBackend, naoUteis, hoje }) {
  return useMemo(() => {
    if (!painel?.ok) return SEM_FLUXO;
    const base = { linhas: painel.classificacao?.linhas, terminos: terminosBackend, naoUteis, hoje };
    return { fluxo: prazoDeFluxo(base), folga: folgaTerminoCarregamento(base) };
  }, [painel, terminosBackend, naoUteis, hoje]);
}

function calcular({ entrada, painel, fluxo, G, anosComRegistro, escala, hoje }) {
  if (!entrada || !painel?.ok) return null;
  if (entrada.entregaDesejada < hoje) {
    return { erroEntrada: "A data de entrega já passou. Escolha uma data a partir de hoje." };
  }
  try {
    return simularVenda({ painel, ambientes: entrada.ambientes, entregaDesejada: entrada.entregaDesejada, fluxo, G, anosComRegistro, escala });
  } catch (erro) {
    // O domínio recusa entrada inválida (fail fast); aqui ela vira aviso no
    // formulário em vez de derrubar a página.
    if (erro instanceof TypeError) return { erroEntrada: erro.message };
    throw erro;
  }
}

/**
 * `{entrada, resultado, definir, limpar, link}`.
 * - `entrada`: `{ambientes:[{classe}], entregaDesejada}` lida do link, ou null;
 * - `definir(entrada)`: grava na URL (sem empilhar histórico);
 * - `resultado`: `simularVenda(...)` | `{erroEntrada}` | null;
 * - `link`: endereço completo para copiar.
 */
export function useSimulacao({ painel, fluxo, G, anosComRegistro, escala, hoje }) {
  const [params, setParams] = useSearchParams();
  const { pathname } = useLocation();
  const bruto = params.get(PARAMETRO_SIMULACAO);
  const entrada = useMemo(() => decodificarSimulacao(bruto), [bruto]);

  const definir = useCallback(
    (nova) => {
      setParams(
        (atuais) => {
          const proximos = new URLSearchParams(atuais);
          if (nova) proximos.set(PARAMETRO_SIMULACAO, codificarSimulacao(nova));
          else proximos.delete(PARAMETRO_SIMULACAO);
          return proximos;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  const limpar = useCallback(() => definir(null), [definir]);

  const resultado = useMemo(
    () => calcular({ entrada, painel, fluxo, G, anosComRegistro, escala, hoje }),
    [entrada, painel, fluxo, G, anosComRegistro, escala, hoje],
  );

  const link = entrada
    ? `${window.location.origin}${pathname}?${PARAMETRO_SIMULACAO}=${codificarSimulacao(entrada)}`
    : null;

  return { entrada, linkInvalido: Boolean(bruto) && !entrada, resultado, definir, limpar, link };
}
